import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect } from 'react'
import { IOrganisation } from 'data-transfers/dto'
import { OrganisationOnboardingStatus } from 'enums'
import { z } from 'zod'
import { makeZodI18nMap } from 'zod-i18n-map'

import { BoundedVariant } from 'components/Bounded'
import { NeedHelpBanner } from 'components/Layouts/components'

import useOrgNewStore from 'store/useOrgNewStore'
import useSubscriptionPlansStore from 'store/useSubscriptionPlansStore'
import { getAvailableSubscriptions } from 'hooks/useSubscriptions/subscription.service'

import { Skeleton } from 'types/layout'
import { PricingPlansDocument } from 'types/prismic'
import { getSSROrganisatation, IMappedPricing, mapPricing } from 'utils'
import { getLayoutParts, getSinglePage } from 'utils/prismic'
import withPageSettings from 'utils/withPageSettings'
import { AuthAccessLevel, UserRole } from 'constants/auth'

const NewOrganisation = dynamic(() => import('components/NewOrganisation'), {
  ssr: false
})

const UserNewPage = ({
  mappedPricing,
  pricingPlans,
  organisation
}: {
  mappedPricing: IMappedPricing
  pricingPlans: PricingPlansDocument
  organisation: IOrganisation
}) => {
  const { t } = useTranslation('zod')
  const setPrices = useSubscriptionPlansStore((state) => state.setPrices)
  const setPlans = useSubscriptionPlansStore((state) => state.setPlans)
  const clear = useSubscriptionPlansStore((state) => state.clear)
  const setTempOrg = useOrgNewStore((state) => state.setTempOrg)
  const setStep = useOrgNewStore((state) => state.setStep)
  const setMajorStep = useOrgNewStore((state) => state.setMajorStep)

  z.setErrorMap(makeZodI18nMap(t))

  useEffect(() => {
    setPlans(pricingPlans.data.slices)

    setPrices(mappedPricing)

    if (organisation && organisation.onboardingStatus) {
      const step = organisation.onboardingStatus
      setTempOrg(organisation)
      setStep(step)

      switch (true) {
        case step < OrganisationOnboardingStatus.Description:
          setMajorStep(0)
          break
        case step < OrganisationOnboardingStatus.PrivacyPolicy:
          setMajorStep(1)
          break
        case step < OrganisationOnboardingStatus.Subscription:
          setMajorStep(2)
          break
        default:
          setMajorStep(3)
      }
    }

    return () => {
      clear()
    }
  }, [
    clear,
    mappedPricing,
    organisation,
    pricingPlans.data.slices,
    setMajorStep,
    setPlans,
    setPrices,
    setStep,
    setTempOrg
  ])

  return (
    <>
      <Head>
        <title>{`Hrizer - Organisation`}</title>
        <meta
          property="og:title"
          content={`Hrizer - Organisation`}
          key="title"
        />
        <meta property="og:site_name" content="Hrizer" key="site_name" />
        <meta property="og:locale" content="en_US" key="locale" />
        <meta
          property="og:locale:alternate"
          content="en_us"
          key="locale:alternate"
        />
        <meta
          name="keywords"
          content={[
            'Hrizer',
            'Job Hunt',
            'Job Search',
            'Job Seeker',
            `New Organisation`
          ].join(' ')}
        />
        <meta
          property="og:description"
          content={`New Organisation`}
          key="og:description"
        />
        <meta
          property="description"
          content={`New Organisation`}
          key="description"
        />
      </Head>

      <div className="grid transition-all md:grid-cols-[60px_1fr_60px] xl:grid-cols-[350px_1fr_50px] 2xl:grid-cols-[350px_1fr_350px]">
        <div className="sticky top-[80px] ml-6 mb-5 flex w-[300px] pl-5 xl:h-[calc(100vh_-_122px_-_1.25rem)]">
          <NeedHelpBanner className={'lg:hidden xl:block'} />
        </div>
        <div className="flex w-full flex-col gap-y-5 overflow-x-hidden pt-10 pb-10 xl:pr-0">
          <NewOrganisation />
        </div>
      </div>
    </>
  )
}

export const getServerSideProps = withPageSettings(
  {
    auth: {
      accessLevel: AuthAccessLevel.Authorized,
      permissions: [UserRole.Organisation, UserRole.Candidate, UserRole.Admin]
    },
    useModal: {
      skeleton: Skeleton.CreateForm,
      title: 'My organisation',
      showsForRoles: [UserRole.Candidate]
    }
  },
  async ({ locale: currentLocale, defaultLocale, previewData, req }) => {
    const locale = currentLocale || defaultLocale || 'en'

    const organisation = await getSSROrganisatation(req.cookies, {
      languageCode: locale,
      includeAllTranslations: true
    })
    if (
      organisation &&
      organisation.onboardingStatus >= OrganisationOnboardingStatus.NewPending
    ) {
      return {
        redirect: {
          permanent: false,
          destination: '/org'
        }
      }
    }
    const pagePartsPromise = getLayoutParts({ locale }, { previewData })

    const i18nPromise = serverSideTranslations(locale, [
      'ad',
      'org',
      'org_form',
      'actions',
      'inputs',
      'meta',
      'auth',
      'pricing_plans',
      'crm',
      'zod'
    ])

    const pricingPlansInfoPromise = getSinglePage(
      'pricing_plans',
      { locale },
      { previewData }
    )

    const subscriptionPricesPromise = getAvailableSubscriptions()

    const [layoutParts, i18n, pricingPlans, subscriptionPrices] =
      await Promise.all([
        pagePartsPromise,
        i18nPromise,
        pricingPlansInfoPromise,
        subscriptionPricesPromise
      ])
    const mappedPricing = mapPricing(subscriptionPrices)

    return {
      props: {
        ...i18n,
        organisation,
        seo: {
          title: 'generalConfig.title',
          metaDescription: 'generalConfig.description'
        },
        pricingPlans,
        mappedPricing,
        internal: {
          layout: {
            ...layoutParts,
            className: 'bg-gray-50',
            boundedVariant: BoundedVariant.None
          }
        }
      }
    }
  }
)

export default UserNewPage
